import {
	createFileRoute,
	useLoaderData,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getReferralCode } from "@/handlers/getReferralCode";

export const Route = createFileRoute("/_logged/onboarding/$club/planos")({
	component: RouteComponent,
	loader: async () => {
		const referralCode = await getReferralCode();

		return { referralCode };
	},
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { club: clubSlug } = useParams({ from: Route.id });
	const { referralCode } = useLoaderData({
		from: Route.id,
	});
	const { capture } = useOnboardingAnalytics("plans", clubSlug);

	const daysToFirstCharge =
		referralCode === "baixador" ? 14 : referralCode ? 30 : 7;

	return (
		<div className="size-full flex items-center justify-start flex-col gap-6 p-6">
			<h1 className="text-balance text-center font-semibold text-gray-900 text-lg md:text-xl lg:text-3xl">
				Escolha seu plano para testar por {daysToFirstCharge} dias de graça
			</h1>

			<span className="max-w-[900px] text-balance px-6 text-center text-gray-600 text-sm md:text-base">
				Você só será cobrado depois do período de testes. Cancele a qualquer
				momento antes e não pagará nada. (A gente te avisa por email quando
				estiver acabando os {daysToFirstCharge} dias)
			</span>

			<a href="https://www.scarf.club/planos" target="_blank" rel="noreferrer">
				<Button variant="ghost">
					Ver comparação completa de planos
					<Icon name="SquareArrowOutUpRight" className="size-4" />
				</Button>
			</a>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
				<Card className="p-6 hover:border-primary! h-fit">
					<div className="flex flex-col">
						<span className="font-semibold text-2xl text-foreground">
							Basic
						</span>
						<span className="text-muted-foreground">
							O essencial para o seu negócio
						</span>
					</div>

					<span>
						<span className="text-base text-muted-foreground">R$</span>
						<span className="font-semibold text-5xl text-foreground">497</span>
						<span className="text-base text-muted-foreground">/ mês</span>
					</span>

					<div className="flex flex-col gap-1">
						{[
							"500 alunos",
							"2 Admins",
							"5 Moderadores",
							"300 GB de armazenamento",
							"400 GB de banda",
							"Domínio e branding próprios",
						].map((it) => (
							<div
								key={it}
								className="flex flex-row items-center justify-start gap-3 text-gray-600"
							>
								<Icon name="Check" className="size-4 text-emerald-600" />

								{it}
							</div>
						))}
					</div>

					<Button
						onClick={() => {
							capture("completed", {
								plan_selected: "scarf-basic",
								plan_price: 497,
								trial_days: daysToFirstCharge,
								has_referral_code: Boolean(referralCode),
							});
							navigate({
								to: "/onboarding/$club/checkout",
								search: { plan: "scarf-basic" },
							});
						}}
					>
						Testar Grátis por {daysToFirstCharge} dias
					</Button>
				</Card>

				<Card className="p-6 hover:border-primary! relative">
					<div className="-top-3 -right-2 md:-right-8 absolute rounded-full bg-yellow-600 px-2 py-1 font-medium text-white text-xs uppercase">
						Mais Popular
					</div>

					<div className="flex flex-col">
						<span className="font-semibold text-2xl text-foreground">Pro</span>
						<span className="text-muted-foreground">
							Para escalar seu negócio
						</span>
					</div>

					<span>
						<span className="text-base text-muted-foreground">R$</span>
						<span className="font-semibold text-5xl text-foreground">997</span>
						<span className="text-base text-muted-foreground">/ mês</span>
					</span>

					<div className="flex flex-col gap-1">
						{[
							"Membros Ilimitados",
							"5 Admins",
							"Moderadores Ilimitados",
							"1TB de armazenamento",
							"2TB de banda",
							"Feeds ilimitados",
							"Cursos e Feeds secretos",
							"Design Avançado",
							"Automações e Filtros Avançados",
							"Addons pela metade do preço",
						].map((it) => (
							<div
								key={it}
								className="flex flex-row items-center justify-start gap-3 text-gray-600"
							>
								<Icon name="Check" className="size-4 text-emerald-600" />

								{it}
							</div>
						))}
					</div>

					<Button
						onClick={() => {
							capture("completed", {
								plan_selected: "scarf-pro",
								plan_price: 997,
								trial_days: daysToFirstCharge,
								has_referral_code: Boolean(referralCode),
							});
							navigate({
								to: "/onboarding/$club/checkout",
								search: { plan: "scarf-pro" },
							});
						}}
					>
						Testar Grátis por {daysToFirstCharge} dias
					</Button>
				</Card>
			</div>
		</div>
	);
}
